import type { Units } from '@better-lights/core';
import { cachedCatalog, loadCatalog, saveCatalog } from '../branding/catalog.ts';
import { useSettings } from '../state/settingsStore.ts';
import { useAccount } from './store.ts';

/**
 * Synced user settings in the personal catalog document (docs/spec/09 §2: `units`). On sign-in the
 * catalog value wins; later local changes are saved back (debounced). Saving needs the `sync`
 * feature: on Free the setting simply stays on this device.
 */
let lastSynced: Units | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
let installed = false;

const isUnits = (v: unknown): v is Units => v === 'imperial' || v === 'metric';

export async function pullSyncedSettings(): Promise<void> {
  const scope = useAccount.getState().me?.personalScope;
  if (!scope) return;
  const c = (await loadCatalog(scope).catch(() => null)) ?? (await cachedCatalog(scope));
  const units = c?.doc.units;
  if (isUnits(units)) {
    lastSynced = units;
    if (useSettings.getState().units !== units) useSettings.getState().patch({ units });
  }
}

export function installSettingsSync(): void {
  if (installed) return;
  installed = true;
  useSettings.subscribe((s, prev) => {
    if (s.units === prev.units || s.units === lastSynced) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      const scope = useAccount.getState().me?.personalScope;
      if (!useAccount.getState().session || !scope) return;
      const units = useSettings.getState().units;
      saveCatalog(scope, { units })
        .then(() => {
          lastSynced = units;
        })
        .catch(() => {}); // Free (402) or offline: stays local
    }, 800);
  });
}
